<template>
  <div>
    <el-input
      v-model="content"
      :placeholder="placeholder"
      @focus="initDir"
    />

    <el-dialog
      :visible.sync="dialogVisible"
      title="关键词选择"
      width="1260px"
      top="5vh"
      class="keyword-dialog"
    >
      <!-- 固定在顶部的搜索和已选区域 -->
      <div class="fixed-header">
        <!-- 已选关键词展示区 -->
        <div v-if="selectedKeywords.length > 0" class="selected-container">
          <div class="selected-label">已选中：</div>
          <div class="selected-items">
            <el-tag
              v-for="(kw, idx) in selectedKeywords"
              :key="'selected-'+idx"
              closable
              @close="removeSelected(kw[filedMap.dCode])"
            >
              {{ kw[filedMap.dName] }}
            </el-tag>
          </div>
        </div>
        <!-- 搜索框 -->
        <div class="search-container">
          <el-input
            ref="searchInput"
            v-model="pinyinCode"
            placeholder="输入拼音首字母搜索 (Ctrl+F 聚焦)"
            size="medium"
            @keydown.native="handleSearchKeydown"
          />
        </div>

      </div>

      <!-- 关键词列表区域 - 使用el-tabs按一级分类展示 -->
      <div
        ref="dicWrap"
        v-loading="loading"
        class="dic-wrap"
        tabindex="-1"
        :class="{ 'focus-area': focusArea === 'keywords' }"
      >
        <el-tabs
          v-if="levelOneGroups.length > 0"
          v-model="activeLevelOne"
          :class="{ 'tabs-focus': focusArea === 'tabs' }"
        >
          <el-tab-pane
            v-for="levelOne in levelOneGroups"
            :key="levelOne.levelOneName"
            :label="levelOne.levelOneName"
            :name="levelOne.levelOneName"
          >
            <div class="container">
              <div
                v-for="(item, index) in levelOne.levelTwoGroups"
                :key="'line-' + index"
                class="dic-item flex flex-row"
              >
                <div :style="labelStyle" class="dic-title-con flex align-center mr-10">
                  <span class="dic-title">
                    {{ item.levelTwoName }}
                  </span>
                </div>
                <div class="keyword-con flex flex-row align-start items-wrap">
                  <span
                    v-for="keyword in item.keywords"
                    :key="keyword.id"
                    :style="keywordStyle"
                    class="keyword cursor-pointer"
                    style="white-space: normal; word-break: break-all;"
                    :class="{
                      selected: isSelected(keyword[filedMap.dCode]),
                      highlighted: keyword === currentTabKeywords[highlightedIndex]
                    }"
                    @click="toggleSelect(keyword)"
                  >
                    {{ keyword[filedMap.dName] }}
                    <i v-if="isSelected(keyword[filedMap.dCode])" class="el-icon-check selected-icon" />
                  </span>
                </div>
              </div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmSelection">确定（Ctrl+Enter）</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { findAll, postData } from "@/api/ik/data/dataApi"
import _ from "lodash"

export default {
  name: "KeyWord",
  props: {
    code: {
      type: String,
      default: "sym"
    },
    keywordWidth: {
      type: Number,
      default: 116
    },
    labelWidth: {
      type: Number,
      default: 82
    },
    value: {
      type: String,
      default: ""
    },
    placeholder: {
      type: String,
      default: "请选择"
    },
    queryParams: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      dialogVisible: false,
      dirList: [],
      loading: false,
      content: "",
      url: "",
      filedMap: {
        id: `symDirectoryId`,
        code: `symDirectoryCode`,
        name: `symDirectoryName`,
        dId: `symDictionaryId`,
        dCode: `symDictionaryCode`,
        dName: `symDictionaryName`
      },
      pinyinCode: "",
      selectedKeywords: [],
      originalContent: "",
      levelOneGroups: [],
      activeLevelOne: "",
      // 键盘交互新增状态
      highlightedIndex: -1,
      focusArea: 'search',
      cols: 6,
      currentTabKeywords: [],
      autoFocusSearchAfterLoad: false
    }
  },
  computed: {
    storeCode() {
      return this.$store.getters['user/getStoreCode']
    },
    keywordStyle() {
      return {
        width: `${this.keywordWidth}px`,
        fontSize: '14px'
      }
    },
    labelStyle() {
      return {
        minWidth: `${this.labelWidth}px`
      }
    }
  },
  watch: {
    value(val) {
      this.content = val
    },
    code: {
      immediate: true,
      handler(newVal) {
        this.dirList = []
        this.selectedKeywords = []
        this.levelOneGroups = []
        this.activeLevelOne = ""
        this.url = `clinic/clinicdic/clinicdiagdic/clinic${newVal}dic`.toLocaleLowerCase()
        this.filedMap = {
          id: `${newVal}DirectoryId`,
          code: `${newVal}DirectoryCode`,
          name: `${newVal}DirectoryName`,
          dId: `${newVal}DictionaryId`,
          dCode: `${newVal}DictionaryCode`,
          dName: `${newVal}DictionaryName`
        }
        if (this.dialogVisible) {
          this.findKeyword()
        }
      }
    },
    queryParams: {
      handler(newVal) {
        if (this.dialogVisible) {
          this.findKeyword()
        }
      },
      deep: true
    },
    dialogVisible(val) {
      if (val) {
        document.addEventListener('keydown', this.handleGlobalKeydown)
        this.$nextTick(() => {
          this.$refs.searchInput?.focus()
          this.focusArea = 'search'
        })
      } else {
        document.removeEventListener('keydown', this.handleGlobalKeydown)
      }
    },
    activeLevelOne() {
      this.$nextTick(() => {
        this.currentTabKeywords = this.getCurrentTabKeywords()
        this.highlightedIndex = this.currentTabKeywords.length > 0 ? 0 : -1
        this.calculateCols()
      })
    }
  },
  created() {
    this.url = `clinic/clinicdic/clinicdiagdic/clinic${this.code}dic`.toLocaleLowerCase()
    this.filedMap = {
      id: `${this.code}DirectoryId`,
      code: `${this.code}DirectoryCode`,
      name: `${this.code}DirectoryName`,
      dId: `${this.code}DictionaryId`,
      dCode: `${this.code}DictionaryCode`,
      dName: `${this.code}DictionaryName`
    }
  },
  mounted() {
    window.addEventListener('resize', this.calculateCols)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateCols)
    document.removeEventListener('keydown', this.handleGlobalKeydown)
  },
  methods: {
    initDir() {
      this.dialogVisible = true
      this.originalContent = this.content
      this.selectedKeywords = []
      this.focusArea = 'search'
      this.highlightedIndex = -1
      this.pinyinCode = ''

      if (this.content) {
        const names = this.content.split(' ').filter(Boolean)
        if (this.dirList.length === 0) {
          this.findKeyword().then(() => {
            this.initSelectedKeywords(names)
          })
          return
        }
        this.initSelectedKeywords(names)
      } else if (this.dirList.length === 0) {
        this.findKeyword()
      }
    },

    initSelectedKeywords(names) {
      this.levelOneGroups.forEach(levelOne => {
        levelOne.levelTwoGroups.forEach(levelTwo => {
          levelTwo.keywords.forEach(kw => {
            if (names.includes(kw[this.filedMap.dName])) {
              this.selectedKeywords.push(kw)
            }
          })
        })
      })
    },

    toggleSelect(keyword) {
      const code = keyword[this.filedMap.dCode]
      const index = this.selectedKeywords.findIndex(kw =>
        kw[this.filedMap.dCode] === code
      )
      if (index > -1) {
        this.selectedKeywords = []
      } else {
        this.selectedKeywords = [keyword]
      }
    },

    isSelected(code) {
      return this.selectedKeywords.some(kw =>
        kw[this.filedMap.dCode] === code
      )
    },

    removeSelected(code) {
      this.selectedKeywords = this.selectedKeywords.filter(kw =>
        kw[this.filedMap.dCode] !== code
      )
    },

    confirmSelection() {
      this.content = this.selectedKeywords
        .map(kw => kw[this.filedMap.dName])
        .join(' ')

      this.selectedKeywords.forEach(keyword => {
        const pd = {
          storeCode: this.storeCode,
          dictionaryCode: keyword[this.filedMap.dCode]
        }
        postData(this.url + "/do/increaseweight", pd)
      })

      this.$emit("input", this.content)
      this.$emit("change", {
        content: this.content,
        code: this.code,
        selectedKeywords: this.selectedKeywords
      })
      this.dialogVisible = false
    },

    findKeyword(options = {}) {
      this.loading = true
      const pd = {
        storeCode: this.storeCode,
        sffectiveSign: "1",
        pinyinCode: this.pinyinCode
      }

      if (this.queryParams && Object.keys(this.queryParams).length > 0) {
        Object.assign(pd, this.queryParams)
      }

      return findAll(this.url, pd)
        .then(res => {
          this.dirList = res.data
          const levelOneGroups = _.groupBy(res.data, this.code + 'DirectoryLevelOneName')

          this.levelOneGroups = Object.keys(levelOneGroups).map(levelOneName => {
            const levelOneItems = levelOneGroups[levelOneName]
            const levelTwoGroups = _.groupBy(levelOneItems, this.code + 'DirectoryLevelTwoName')
            const levelTwoArray = Object.keys(levelTwoGroups).map(levelTwoName => ({
              levelTwoName,
              keywords: levelTwoGroups[levelTwoName]
            }))
            return { levelOneName, levelTwoGroups: levelTwoArray }
          })

          if (this.levelOneGroups.length > 0) {
            this.activeLevelOne = this.levelOneGroups[0].levelOneName
          }

          this.$nextTick(() => {
            this.currentTabKeywords = this.getCurrentTabKeywords()
            this.calculateCols()

            // 搜索状态下唯一结果自动选中，并重新加载全部数据方便继续输入
            if (this.pinyinCode && this.currentTabKeywords.length === 1) {
              this.selectedKeywords = [this.currentTabKeywords[0]]
              this.pinyinCode = ''
              this.autoFocusSearchAfterLoad = true
              setTimeout(() => this.findKeyword(), 50)
            } else {
              this.highlightedIndex = this.currentTabKeywords.length > 0 ? 0 : -1
            }
          })
        })
        .finally(() => {
          this.loading = false
          this.pinyinCode = ''

          if (this.autoFocusSearchAfterLoad) {
            this.autoFocusSearchAfterLoad = false
            this.focusArea = 'search'
            this.$nextTick(() => this.$refs.searchInput?.focus())
          } else if (options.focusToKeywords) {
            this.focusArea = 'keywords'
            this.highlightedIndex = this.currentTabKeywords.length > 0 ? 0 : -1
            this.$nextTick(() => {
              this.$refs.dicWrap?.focus()
              this.scrollToHighlighted()
            })
          }
        })
    },

    getCurrentTabKeywords() {
      const activeGroup = this.levelOneGroups.find(g => g.levelOneName === this.activeLevelOne)
      if (!activeGroup) return []
      const keywords = []
      activeGroup.levelTwoGroups.forEach(lt => {
        keywords.push(...lt.keywords)
      })
      return keywords
    },

    calculateCols() {
      this.$nextTick(() => {
        const container = this.$refs.dicWrap?.querySelector('.keyword-con')
        const firstItem = container?.querySelector('.keyword')
        if (container && firstItem) {
          const containerWidth = container.clientWidth
          const itemWidth = firstItem.offsetWidth + 8
          this.cols = Math.max(1, Math.floor(containerWidth / itemWidth))
        } else {
          this.cols = 6
        }
      })
    },

    scrollToHighlighted() {
      this.$nextTick(() => {
        const highlighted = this.$refs.dicWrap?.querySelector('.keyword.highlighted')
        if (highlighted) {
          highlighted.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    },

    handleSearchKeydown(e) {
      if (e.key === 'Enter') {
        e.preventDefault()
        this.findKeyword({ focusToKeywords: true })
      }
    },

    handleGlobalKeydown(e) {
      if (!this.dialogVisible) return

      // Ctrl+F 重新聚焦搜索框
      if (e.ctrlKey && e.key === 'f') {
        e.preventDefault()
        this.focusArea = 'search'
        this.$nextTick(() => {
          const input = this.$refs.searchInput
          if (input) {
            input.focus()
            input.$refs.input?.select()
          }
        })
        return
      }

      // Ctrl+Z 撤销选中（单选模式下直接清空）
      if (e.ctrlKey && e.key === 'z') {
        if (this.selectedKeywords.length > 0) {
          e.preventDefault()
          this.selectedKeywords = []
        }
        return
      }

      // Ctrl+Enter 确定选择
      if (e.ctrlKey && e.key === 'Enter') {
        e.preventDefault()
        this.confirmSelection()
        return
      }

      // Shift+左右切换焦点区域（tabs <-> 关键词列表）
      if (e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault()
        if (e.key === 'ArrowLeft') {
          this.focusArea = 'tabs'
          if (!this.activeLevelOne && this.levelOneGroups.length > 0) {
            this.activeLevelOne = this.levelOneGroups[0].levelOneName
          }
          this.$nextTick(() => {
            this.currentTabKeywords = this.getCurrentTabKeywords()
            this.highlightedIndex = -1
          })
        } else {
          this.focusArea = 'keywords'
          this.highlightedIndex = this.currentTabKeywords.length > 0 ? 0 : -1
          this.$nextTick(() => {
            this.$refs.dicWrap?.focus()
            this.scrollToHighlighted()
          })
        }
        return
      }

      // Tabs 区域导航
      if (this.focusArea === 'tabs') {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault()
          const tabs = this.levelOneGroups.map(g => g.levelOneName)
          const idx = tabs.indexOf(this.activeLevelOne)
          if (e.key === 'ArrowLeft' && idx > 0) {
            this.activeLevelOne = tabs[idx - 1]
          } else if (e.key === 'ArrowRight' && idx < tabs.length - 1) {
            this.activeLevelOne = tabs[idx + 1]
          }
          return
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          this.focusArea = 'keywords'
          this.highlightedIndex = this.currentTabKeywords.length > 0 ? 0 : -1
          this.$nextTick(() => {
            this.$refs.dicWrap?.focus()
            this.scrollToHighlighted()
          })
          return
        }
      }

      // 关键词列表区域导航
      if (this.focusArea === 'keywords') {
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
          e.preventDefault()
          const total = this.currentTabKeywords.length
          if (!total) return
          let idx = this.highlightedIndex
          if (idx < 0) idx = 0

          if (e.key === 'ArrowUp') idx -= this.cols
          else if (e.key === 'ArrowDown') idx += this.cols
          else if (e.key === 'ArrowLeft') idx -= 1
          else if (e.key === 'ArrowRight') idx += 1

          if (idx < 0) idx = 0
          if (idx >= total) idx = total - 1
          this.highlightedIndex = idx
          this.scrollToHighlighted()
          return
        }

        if (e.key === ' ') {
          e.preventDefault()
          const kw = this.currentTabKeywords[this.highlightedIndex]
          if (kw) this.toggleSelect(kw)
          return
        }
      }

      // 阻止普通 Enter 触发 dialog 默认确定按钮
      if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey && !e.altKey) {
        if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
          e.preventDefault()
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.fixed-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: white;
  padding: 12px 20px;
  margin: 0 -10px;
  border-bottom: 1px solid #E5E6EB;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-height: 30vh;
  overflow-y: auto;
  flex-shrink: 0;
}

.search-container {
  margin-bottom: 12px;
  width: 100%;
  :deep(.el-input) {
    .el-input__inner {
      border-radius: 6px;
    }
  }
}

.selected-container {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;

  .selected-label {
    font-weight: bold;
    color: #165DFF;
    min-width: 60px;
    margin-top: 4px;
    flex-shrink: 0;
  }

  .selected-items {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    flex: 1;

    :deep(.el-tag) {
      height: auto;
      white-space: normal;
      word-break: break-all;
      line-height: 1.4;
      padding: 4px 8px;
    }
  }
}

.dic-wrap {
  padding: 0 10px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  margin-top: 10px;
  outline: none;
  border: 1px solid transparent;
  border-radius: 4px;
  transition: border-color 0.2s, box-shadow 0.2s;

  &.focus-area {
    border-color: #165DFF;
    box-shadow: 0 0 0 2px rgba(22, 93, 255, 0.15);
  }
}

.dic-item {
  border-top: 1px solid #E5E6EB;
  padding: 16px 0;

  &:first-child {
    border-top: 1px solid transparent;
  }

  .dic-title-con {
    border-right: 1px solid #E5E6EB;

    .dic-title {
      font-family: PingFang SC, PingFang SC, serif;
      font-weight: bold;
      font-size: 14px;
      color: #165DFF;
      line-height: 22px;
      white-space:normal; word-break:break-all;overflow:hidden;
    }
  }

  .keyword-con {
    .keyword {
      position: relative;
      border-radius: 2px;
      font-weight: normal;
      font-size: 14px;
      color: #000;
      line-height: 22px;
      text-align: center;
      margin: 4px;
      padding: 0 5px;
      transition: all 0.3s;
      word-break: break-all;

      &:hover {
        background: #f0f7ff;
        color: #165DFF;
      }

      &.selected {
        background: #f0f7ff;
        color: #165DFF;
        font-weight: bold;
      }

      &.highlighted {
        outline: 2px solid #ffa940;
        outline-offset: 2px;
        border-radius: 4px;
      }

      .selected-icon {
        position: absolute;
        top: -5px;
        right: -5px;
        color: #fff;
        background: #165DFF;
        border-radius: 50%;
        font-size: 10px;
        width: 14px;
        height: 14px;
        line-height: 14px;
      }
    }
  }
}

:deep(.el-dialog__body) {
  padding: 15px 20px;
  position: relative;
}

.keyword-dialog :deep(.el-dialog__body) {
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  height: 60vh;
}

.keyword-dialog :deep(.el-dialog) {
  display: flex;
  flex-direction: column;

  .el-dialog__body {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
}

:deep(.el-tabs) {
  width: 100%;

  .el-tabs__header {
    margin-bottom: 16px;
  }

  .el-tabs__content {
    height: calc(100% - 40px);
    overflow: hidden;
  }
}

.el-tabs.tabs-focus {
  :deep(.el-tabs__active-bar) {
    background-color: #ffa940;
  }
  :deep(.el-tabs__item.is-active) {
    color: #ffa940;
    font-weight: bold;
  }
}
</style>
